import { Visit } from "@services/requests/housien-models/Visit";

interface ConflictResult {
  hasConflict: boolean;
  conflictVisit?: Visit;
}

/**
 * Parses an ISO datetime into date and time parts using UTC fields, treating
 * stored datetimes as already normalized to BRT.
 *
 * @param isoString - ISO datetime string to parse.
 * @returns The `date` (`YYYY-MM-DD`) and `time` (`HH:MM`).
 */
export function parseBrtDateTime(isoString: string): { date: string; time: string } {
  const d = new Date(isoString);
  const date = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  const time = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  return { date, time };
}

/**
 * Determines whether a visit should be excluded from conflict detection: it is
 * missing a datetime, cancelled, or the visit being edited.
 *
 * @param v - Visit to evaluate.
 * @param excludeVisitId - ID of the visit currently being edited, if any.
 * @returns True when the visit must not count as a conflict.
 */
function isExcludedVisit(v: Visit, excludeVisitId?: string): boolean {
  if (!v.visit_datetime) return true;
  if (v.status === "cancelled") return true;
  if (excludeVisitId && v.visit_id === excludeVisitId) return true;
  return false;
}

/**
 * Collects the set of occupied time slots on a given date, ignoring excluded
 * visits (cancelled, dateless, or the one being edited).
 *
 * @param visits - Candidate visits to scan.
 * @param selectedDate - Target date (`YYYY-MM-DD`).
 * @param excludeVisitId - Visit ID to exclude (the one being edited).
 * @returns A set of `HH:MM` slots already taken on that date.
 */
export function getOccupiedSlots(
  visits: Visit[],
  selectedDate: string,
  excludeVisitId?: string,
): Set<string> {
  const slots = new Set<string>();
  for (const v of visits) {
    if (isExcludedVisit(v, excludeVisitId)) continue;
    const { date, time } = parseBrtDateTime(v.visit_datetime!);
    if (date === selectedDate) slots.add(time);
  }
  return slots;
}

/**
 * Finds the first visit that collides with the selected date and time,
 * skipping excluded visits, to warn against double-booking.
 *
 * @param visits - Candidate visits to scan.
 * @param selectedDate - Target date (`YYYY-MM-DD`).
 * @param selectedTime - Target time (`HH:MM`).
 * @param excludeVisitId - Visit ID to exclude (the one being edited).
 * @returns Whether a conflict exists and, if so, the conflicting visit.
 */
export function findTimeConflict(
  visits: Visit[],
  selectedDate: string,
  selectedTime: string,
  excludeVisitId?: string,
): ConflictResult {
  const conflict = visits.find((v) => {
    if (isExcludedVisit(v, excludeVisitId)) return false;
    const { date, time } = parseBrtDateTime(v.visit_datetime!);
    return date === selectedDate && time === selectedTime;
  });
  return conflict ? { hasConflict: true, conflictVisit: conflict } : { hasConflict: false };
}
